import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { Capabilities } from "@/components/calv/Capabilities";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/en/services/social-media-marketing")({
  head: () => {
    const h = pageHead("socialMediaMarketing", "en");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "Social Media Marketing — Organic & Paid Integrated",
          description: "Methodical social media marketing for mid-sized companies and e-commerce. Organic and paid integrated across Meta, TikTok, LinkedIn, and Pinterest.",
          url: "/en/services/social-media-marketing",
          serviceType: "Social Media Marketing",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Home", path: "/en" },
          { name: "Services", path: "/en/services" },
          { name: "Social Media Marketing", path: "/en/services/social-media-marketing" },
        ])),
      ],
    };
  },
  component: SocialMediaMarketingEN,
});

const platforms = [
  { tag: "Meta", title: "Instagram & Facebook", body: "Broad B2C reach, strong targeting mechanics, integrated shopping layer. First choice for e-commerce with AOV €30–300, visualizable products, and a visual language that creates stop-power. In Tier-1 mandates, Reels account for 60–80% of total reach generation." },
  { tag: "TikTok", title: "Young audiences", body: "High organic reach potential at low CPM, dominant in the 18–34 demographic. Sensible for brands with creative content capacity and tolerance for experimental style. The algorithm rewards frequency and authenticity — well-produced gloss often performs worse than raw, contextual content." },
  { tag: "LinkedIn", title: "B2B decision-makers", body: "Dominant B2B channel, especially for sales cycles between 3 and 18 months. First choice for complex services, software sales, and high-ticket mandates. Managing-director visibility works substantially stronger than brand-account visibility." },
  { tag: "Pinterest", title: "Inspiration search", body: "Underrated channel with long-term search mechanics — pins remain discoverable for 6–12 months. Substantive for lifestyle, home, weddings, fashion, DIY, garden — anywhere inspiration-search still carries substantive search volumes." },
];

const capabilities = [
  { name: "Platform strategy", detail: "Audience fit, not trend logic" },
  { name: "Organic content cadence", detail: "3–7 posts/week, platform-specific" },
  { name: "Paid amplification", detail: "50–70% of budget on organic winners" },
  { name: "Creator & UGC strategy", detail: "Substantive selection, not spread" },
  { name: "Server-side conversion tracking", detail: "CAPI · Conversions API · Events API" },
  { name: "Attribution & incrementality", detail: "DDA, brand lift, geo holdouts" },
  { name: "Creative refresh pipeline", detail: "Monthly rhythm, picazzoo-supported" },
  { name: "Economic pre-validation", detail: "AOV, funnel, platform fit" },
];

const budgetSplit = [
  ["50–70%", "Paid amplification of well-performing organic content — instead of blind paid posting of new creatives."],
  ["20–30%", "Strategically planned paid activation — product launches, seasonality, escalations."],
  ["10–20%", "Test reserve for new formats, new audiences, new platform features."],
];

const velocity = [
  { tag: "Instagram", title: "3–5 posts + 1–2 Reels/week", body: "Stable reach development inside 2026 platform logic — Reels dominate reach, static posts hold the brand." },
  { tag: "TikTok", title: "4–7 videos/week", body: "Substantive algorithm visibility. Frequency and authenticity beat production gloss — contextual content before polish." },
  { tag: "LinkedIn", title: "3–5 posts/week", body: "Company page plus 1–3 personal accounts of key people. Managing-director visibility as an additional trust lever." },
  { tag: "Pinterest", title: "5–10 pins/week", body: "Aligned to seasonal search patterns. Pins stay visible for months — effort amortizes long-term." },
];

const notFit = [
  { tag: "Not a fit", title: "High-priced B2B with small TAM", body: "When the audience consists of 200 decision-makers in DACH, account-based sales plus targeted conference presence is more effective. LinkedIn can flank, but is not the primary tool." },
  { tag: "Not a fit", title: "Commodity without visual differentiation", body: "Social media lives on visual substance. Search-oriented performance marketing (SEA, SEO) compensates for missing visual differentiation via search intent." },
  { tag: "Not a fit", title: "Heavily regulated industries", body: "Pharma, tobacco, gambling, weapons, parts of financial services — platform advertising policies leave little substantive room for economic steering." },
];

const figures = [
  ["3–7", "posts per week, typical content velocity"],
  ["€1–50k", "monthly paid budgets in mandates"],
  ["2–8%", "engagement rate on Instagram in Tier-1 mandates"],
  ["+30–60%", "attribution recovery through server-side CAPI"],
];

function SocialMediaMarketingEN() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Social Media Marketing · since 2013"
          headline={<>Social media, <em>thought through integrated.</em></>}
          lead="Organic and paid social are not two disciplines, but two levers of one integrated mechanic. We run social media marketing across Meta, TikTok, LinkedIn, and Pinterest as a shared system — controlled, data-driven, without black-box promises."
          metaStrip={[
            "3–7 posts/week typical velocity",
            "€1–50k monthly paid budgets",
            "Karlstadt am Main · Germany · DACH",
          ]}
          breadcrumbs={[
            { label: "Home", to: "/en" },
            { label: "Services", to: "/en/services" },
            { label: "Social Media Marketing" },
          ]}
        />

        <Section number="02" kicker="Position" headline={<>What social media marketing <em>methodically is.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Social platforms do not work on reach alone. They work through three integrated levers: <strong className="font-semibold text-ink">content substance</strong> (what is being said), <strong className="font-semibold text-ink">distribution mechanics</strong> (organic reach plus paid amplification), and <strong className="font-semibold text-ink">community density</strong> (who interacts, who shares, who recommends). Leaving out any one of the three reduces the work to symbolic politics — colorful posts without economic effect.</p>
              <p className="mb-5">Calvarius approaches the social media setup from economic pre-validation outward. Before content calendars or paid budgets are discussed, the question must be answered: does the business model carry the social media mechanics at all? Which platforms match the actual audience? What does the funnel from social touchpoint to purchase look like, and which share of it is cleanly measurable?</p>
              <p>This order distinguishes methodical social media work from operational posting. Anyone who starts with the content calendar has skipped the economic question and operates from week one on an unverified assumption.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Platform strategy" headline={<>Four platforms, <em>four substance profiles.</em></>}
          caption="Not every brand belongs on every platform — platform selection follows audience reality, not trend.">
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {platforms.map((p) => (
                <div key={p.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{p.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{p.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{p.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-ink-3">
              <strong className="font-semibold text-ink">Snapchat, Reddit, X, Bluesky.</strong> Only sensible where the audience is actively purchase-affine. Blanket presence on all platforms is wastage, not strategy.
            </p>
            <div className="mt-6 flex flex-wrap gap-6">
              <Link to="/en/services/performance-marketing/meta-ads" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">Meta Ads — deep dive →</Link>
              <Link to="/en/services/performance-marketing/tiktok-ads" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">TikTok Ads — deep dive →</Link>
              <Link to="/en/services/performance-marketing/linkedin-ads" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">LinkedIn Ads — deep dive →</Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Organic & Paid" headline={<>Organic and paid as <em>integrated mechanics.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">The separation between organic social and paid social is an operational convention, not a methodical distinction. Both operate inside the same algorithmic space, both require substantive content, both are measured against the same KPIs. Strict separation loses the leverage effect of both sides.</p>
            </div>
            <div className="mt-8 grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-3">
              {budgetSplit.map(([share, body]) => (
                <div key={share} className="bg-white p-7">
                  <div className="font-display text-2xl text-cyan-deep">{share}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{body}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-ink-3">This split protects against two frequent mistakes: against "we do only organic" (low reach, high dependence on algorithm luck) and against "we do only paid" (no authentic brand build-up, high CPM dependence, fragile performance during platform updates).</p>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="Content velocity" headline={<>Ranges from <em>active mandates.</em></>}
          caption="Substance indicators, not guarantees — substantially below means measurable reach loss over weeks.">
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {velocity.map((v) => (
                <div key={v.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{v.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{v.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{v.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-ink-3">For creator strategies (UGC, influencer collaborations, brand ambassadors), the substance rule applies: substantive selection over reach maximization. <strong className="font-semibold text-ink">Three reliable creators over six months</strong> are more effective than twenty one-off collaborations — authenticity emerges from repetition, not spread.</p>
          </RevealOnScroll>
        </Section>

        <Section number="06" kicker="Performance & attribution" headline={<>The attribution-hardest channel — <em>methodically manageable.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Click-through rates are often low, view-through effects high, cross-device journeys dominant. <em>Last-click attribution systematically underrates social media by 30–60%</em> — assessing performance purely by last-click logic inevitably leads to the wrong investment decision.</p>
              <p className="mb-3">Methodologically substantive performance measurement in a social media mandate:</p>
              <ul className="mb-5 space-y-2 text-[15px] text-ink-2">
                <li>• Data-driven or position-based attribution instead of last-click</li>
                <li>• Conversions API (CAPI) for Meta, comparable server-side integrations for TikTok and LinkedIn</li>
                <li>• Brand-lift studies for high-reach campaigns with substantive budget</li>
                <li>• Incrementality tests (geo holdouts) for budget-relevant decisions</li>
                <li>• Cross-channel view on the funnel — social is rarely the only touchpoint</li>
              </ul>
              <p>→ Deep dive on tracking substance: <Link to="/en/tools/performance-marketing-checklist" className="underline">Performance Marketing Checklist, Phase 02</Link></p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="Economic pre-validation" headline={<>Four questions, <em>before budget flows.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5"><strong className="font-semibold text-ink">AOV viability.</strong> Does the average order value carry the platform-typical CPMs (€1–15) and CPCs (€0.30–3) at the required conversion rate? Below an AOV of €30, the math gets difficult in most industries.</p>
              <p className="mb-5"><strong className="font-semibold text-ink">Funnel mechanics.</strong> Is there a substantive conversion path from social to purchase? Or is social a pure awareness channel inside a longer funnel that closes through other channels? Both are valid — but the expectation definition must match the mechanics.</p>
              <p className="mb-5"><strong className="font-semibold text-ink">Content capacity.</strong> Does the company have the resources for the necessary content frequency — in-house, via agency, or with a creator? Without this substance, even the best setup will not carry.</p>
              <p className="mb-5"><strong className="font-semibold text-ink">Platform fit.</strong> Is the audience actually active and purchase-affine on the targeted platform, or are they only passive consumers there? Activity and purchase readiness are not the same.</p>
              <p>→ Tool for the pre-validation: <Link to="/en/tools/profitability-calculator" className="underline">Viability Calculator</Link></p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="When not" headline={<>When social media marketing <em>is not the right lever.</em></>}
          caption="Honest limitation statements are part of the Calvarius initial conversation.">
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-3">
              {notFit.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-2">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="09" kicker="What we deliver" headline={<>What our <em>social media operation</em> concretely contains.</>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="10" kicker="Mandates in figures" headline={<>Four figures from <em>active mandates.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2 md:grid-cols-4">
              {figures.map(([n, label]) => (
                <div key={label} className="bg-white p-7">
                  <div className="font-display text-3xl text-cyan-deep">{n}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{label}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="11" kicker="Concrete step">
          <CTABlock
            headline={<>More budget is not a strategy. <em>Methodology is.</em></>}
            body="In a first conversation we clarify whether social media marketing is the right lever for your economic situation — and if not, we say so honestly and recommend alternatives."
            primary={{ label: "Request a strategy conversation", to: "/en/contact" }}
            secondary={{ label: "Test the methodical tools", to: "/en/tools" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
